import { connect, tokenAuthenticator } from 'nats.ws';
import type { NatsConnection, JetStreamManager, JetStreamClient } from 'nats.ws';

class NATSClient {
  private connection: NatsConnection | null = null;
  private jsClient: JetStreamClient | null = null; // renamed from jetstream to avoid name conflict
  private jsManager: JetStreamManager | null = null;
  private statusCallbacks: Array<(status: { isConnected: boolean; error?: string; lastConnected?: Date | null }) => void> = [];
  private lastConnectedTime: Date | null = null;
  
  // Store connection parameters for reconnection
  private lastServers: string[] = [];
  private lastToken: string | undefined;
  /**
   * Optional NATS `user` presented alongside the JWT. Demo mode sets this to
   * "demo" so the auth callout issues demo-scoped permissions for the same
   * token. Stored like `lastToken` so every reconnect path keeps the identity.
   */
  private lastNatsUser: string | undefined;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private isReconnecting = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionReady = false; // Tracks actual wire-level readiness

  // Add a method to subscribe to status changes
  onStatusChange(callback: (status: { isConnected: boolean; error?: string; lastConnected?: Date | null }) => void) {
    this.statusCallbacks.push(callback);
    // Call immediately with current status
    callback({
      isConnected: this.isConnected(),
      lastConnected: this.lastConnectedTime
    });
  }

  // Remove status change callback
  offStatusChange(callback: (status: { isConnected: boolean; error?: string; lastConnected?: Date | null }) => void) {
    const index = this.statusCallbacks.indexOf(callback);
    if (index > -1) {
      this.statusCallbacks.splice(index, 1);
    }
  }

  private notifyStatusChange(status: { isConnected: boolean; error?: string }) {
    this.statusCallbacks.forEach(callback => {
      callback({
        ...status,
        lastConnected: this.lastConnectedTime
      });
    });
  }

  async connect(servers: string[], token?: string, natsUser?: string): Promise<void> {
    // Store parameters for reconnection
    this.lastServers = servers;
    this.lastToken = token;
    this.lastNatsUser = natsUser;
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    
    try {
      // For WebSocket connections, we need different options
      const isWebSocket = servers.some(server => server.startsWith('ws://') || server.startsWith('wss://'));
      
      const options: any = {
        servers,
        timeout: 10000, // 10 seconds timeout
        reconnect: true,
        maxReconnectAttempts: -1, // Infinite reconnection attempts
        reconnectTimeWait: 2000, // 2 seconds between attempts
        reconnectJitter: 1000, // Add jitter to prevent thundering herd
        pingInterval: 30000, // Keep connection alive with pings every 30s
        maxPingOut: 3, // Allow 3 missed pongs before considering connection dead
        ignoreClusterUpdates: true, // Don't use gossiped internal cluster IPs — browser can't reach them
      };

      // For WebSocket connections, don't set TLS options - let the protocol handle it
      if (isWebSocket) {
        console.log('Configuring WebSocket connection');
        // Don't set TLS options for WebSocket - the wss:// protocol handles encryption
      } else {
        // For TCP connections, configure TLS if needed
        options.tls = {};
      }

      // Add authentication if token is provided. Use a token PROVIDER (not a
      // static token) so nats.ws reads the latest `this.lastToken` on every
      // (re)connect, including its internal auto-reconnect after a stale/expired
      // connection. A static token would be replayed after expiry and the server
      // would reject every reconnect with AUTHORIZATION_VIOLATION in a loop.
      if (token) {
        options.authenticator = tokenAuthenticator(() => this.lastToken || '');
      }

      // Present a NATS username alongside the token when one is configured
      // (demo mode). nats.ws merges every configured credential source into a
      // single CONNECT, so this yields `{ user, auth_token }` rather than
      // replacing the token authenticator above.
      if (this.lastNatsUser) {
        options.user = this.lastNatsUser;
      }

      console.log('Attempting to connect to NATS servers:', servers);
      console.log('Connection options:', { ...options, token: token ? '[REDACTED]' : undefined });
      
      this.connection = await connect(options);
      this.jsClient = this.connection.jetstream();
      this.jsManager = await this.connection.jetstreamManager();
      this.lastConnectedTime = new Date();
      this.connectionReady = true;

      console.log('Successfully connected to NATS cluster');
      this.notifyStatusChange({ isConnected: true });

      // Monitor connection status for reconnection events
      this.monitorConnectionStatus();

      // Handle connection events
      this.connection.closed().then((err) => {
        console.log('NATS connection closed', err);
        this.connection = null;
        this.jsClient = null;
        this.jsManager = null;
        this.connectionReady = false;
        this.notifyStatusChange({ 
          isConnected: false, 
          error: err ? String(err) : undefined 
        });
        
        // Attempt automatic reconnection if we have stored parameters
        if (this.lastServers.length > 0 && !this.isReconnecting) {
          console.log('🔄 Connection closed unexpectedly, attempting automatic reconnection...');
          this.attemptReconnect();
        }
      });

    } catch (error) {
      console.error('Failed to connect to NATS:', error);
      
      // Provide more specific error information
      let errorMessage = String(error);
      if (error instanceof Error && error.message) {
        if (error.message.includes('tls')) {
          errorMessage = 'TLS/SSL connection failed. WebSocket encryption may not be configured properly.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Connection timeout. Server may be unreachable.';
        } else if (error.message.includes('authorization')) {
          errorMessage = 'Authentication failed. Check credentials.';
        } else if (error.message.includes('websocket')) {
          errorMessage = 'WebSocket connection failed. Check server URL and firewall settings.';
        }
      }
      
      this.notifyStatusChange({ 
        isConnected: false, 
        error: errorMessage 
      });
      throw error;
    }
  }

  // Monitor connection status and handle reconnections
  private async monitorConnectionStatus(): Promise<void> {
    if (!this.connection) return;

    // Listen for status events
    (async () => {
      for await (const status of this.connection!.status()) {
        switch (status.type) {
          case 'disconnect':
            console.warn('⚠️ NATS disconnected, will attempt reconnect...');
            this.connectionReady = false;
            this.notifyStatusChange({ isConnected: false, error: 'Disconnected, reconnecting...' });
            break;
          case 'reconnecting':
            console.log('🔄 NATS reconnecting...');
            this.connectionReady = false;
            this.notifyStatusChange({ isConnected: false, error: 'Reconnecting...' });
            break;
          case 'reconnect':
            console.log('✅ NATS reconnected successfully');
            this.connectionReady = true;
            this.lastConnectedTime = new Date();
            this.reconnectAttempts = 0; // Reset reconnect counter on success
            this.notifyStatusChange({ isConnected: true });
            // Dispatch event so the app knows to refresh data
            window.dispatchEvent(new CustomEvent('nats-reconnected'));
            break;
          case 'error':
            console.error('❌ NATS error:', status.data);
            break;
          case 'pingTimer':
            // Ping timer events are normal, don't log
            break;
          default:
            console.log(`📡 NATS status: ${status.type}`);
        }
      }
    })().catch(err => console.error('NATS status monitor error:', err));
  }

  // Attempt to reconnect to NATS with exponential backoff
  private async attemptReconnect(): Promise<void> {
    if (this.isReconnecting) {
      console.log('🔄 Reconnection already in progress, skipping...');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached. Manual refresh required.`);
      this.notifyStatusChange({ 
        isConnected: false, 
        error: `Connection lost. Max reconnection attempts reached. Please refresh the page.` 
      });
      window.dispatchEvent(new CustomEvent('nats-reconnect-failed'));
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    console.log(`🔄 Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms...`);
    
    this.notifyStatusChange({ 
      isConnected: false, 
      error: `Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})` 
    });

    // Clear any existing timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        // Get fresh token from Keycloak if available
        let token = this.lastToken;
        if ((window as any).keycloak?.authenticated) {
          try {
            await (window as any).keycloak.updateToken(30);
            token = (window as any).keycloak.token;
            this.lastToken = token;
          } catch (tokenErr) {
            console.warn('Failed to refresh token during reconnect:', tokenErr);
          }
        }

        // Attempt to connect
        console.log(`🔄 Reconnecting to NATS servers:`, this.lastServers);
        await this.connect(this.lastServers, token, this.lastNatsUser);
        
        console.log('✅ NATS reconnection successful');
        this.reconnectAttempts = 0;
        this.isReconnecting = false;
        
        // Notify the app that connection is recovered
        window.dispatchEvent(new CustomEvent('nats-reconnected'));
        window.dispatchEvent(new CustomEvent('connection-recovered'));
        
      } catch (error) {
        console.error(`❌ Reconnection attempt ${this.reconnectAttempts} failed:`, error);
        this.isReconnecting = false;
        
        // Try again
        this.attemptReconnect();
      }
    }, delay);
  }

  // Force a reconnection attempt (can be called externally)
  async forceReconnect(): Promise<void> {
    console.log('🔄 Force reconnecting to NATS...');
    
    // Store current connection info before disconnect
    const servers = this.lastServers.length > 0 
      ? this.lastServers 
      : [import.meta.env.VITE_NATS_URL || 'wss://natsws.enlixy.com:443'];
    const token = this.lastToken || (window as any).keycloak?.token;
    const natsUser = this.lastNatsUser;
    
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    
    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Disconnect if still connected (without clearing servers)
    if (this.connection && !this.connection.isClosed()) {
      await this.connection.close();
      this.connection = null;
      this.jsClient = null;
      this.jsManager = null;
    }
    
    // Restore servers and token for reconnection
    this.lastServers = servers;
    this.lastToken = token;
    this.lastNatsUser = natsUser;
    
    // Attempt reconnection
    await this.attemptReconnect();
  }

  // Update stored token for future reconnections (does NOT tear down current connection)
  updateToken(newToken: string): void {
    console.log('🔐 NATSClient: Token updated for future reconnections');
    this.lastToken = newToken;
  }

  async disconnect(): Promise<void> {
    // Cancel any pending reconnection attempts
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    
    if (this.connection) {
      // Temporarily clear servers to prevent auto-reconnect on intentional disconnect
      const wasIntentional = true;
      if (wasIntentional) {
        this.lastServers = [];
        this.lastToken = undefined;
        this.lastNatsUser = undefined;
      }
      
      await this.connection.close();
      this.connection = null;
      this.jsClient = null;
      this.jsManager = null;
      this.connectionReady = false;
      console.log('Disconnected from NATS');
      this.notifyStatusChange({ isConnected: false });
    }
  }

  async publish(subject: string, data: string | Uint8Array): Promise<void> {
    if (!this.connection) {
      throw new Error('Not connected to NATS');
    }
    
    const payload = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    this.connection.publish(subject, payload);
  }

  async request(subject: string, data?: string | object, timeout: number = 10000): Promise<any> {
    if (!this.connection || !this.connectionReady) {
      // Try a quick reconnect before giving up
      if (this.lastServers.length > 0 && !this.isReconnecting) {
        console.log('🔄 NATS not ready, attempting reconnect before request...');
        try {
          let token = this.lastToken;
          if ((window as any).keycloak?.authenticated) {
            try {
              await (window as any).keycloak.updateToken(30);
              token = (window as any).keycloak.token;
              this.lastToken = token;
            } catch { /* use existing token */ }
          }
          await this.connect(this.lastServers, token, this.lastNatsUser);
        } catch (reconnErr) {
          console.error('🔄 Quick reconnect failed:', reconnErr);
          throw new Error('Not connected to NATS');
        }
      } else {
        throw new Error('Not connected to NATS');
      }
    }

    try {
      const payload = data 
        ? new TextEncoder().encode(typeof data === 'string' ? data : JSON.stringify(data))
        : undefined;
      
      const __reqStart = performance.now();
      const response = await this.connection!.request(subject, payload, { timeout });
      const __netMs = performance.now() - __reqStart;
      const responseText = new TextDecoder().decode(response.data);
      const __sizeKB = (responseText.length / 1024).toFixed(1);

      try {
        const __parseStart = performance.now();
        const parsed = JSON.parse(responseText);
        const __parseMs = performance.now() - __parseStart;
        console.log(`⏱️ [PERF] NATS ${subject}: network=${__netMs.toFixed(0)}ms, parse=${__parseMs.toFixed(0)}ms, payload=${__sizeKB}KB`);
        return parsed;
      } catch {
        console.log(`⏱️ [PERF] NATS ${subject}: network=${__netMs.toFixed(0)}ms, payload=${__sizeKB}KB (non-JSON)`);
        return responseText;
      }
    } catch (error) {
      console.error(`NATS request failed for subject ${subject}:`, error);
      
      // If the request itself fails due to connection issues, mark as not ready
      if (error instanceof Error && (error.message.includes('DISCONNECT') || error.message.includes('closed'))) {
        this.connectionReady = false;
      }
      
      // Provide more helpful error messages for common issues
      if (error instanceof Error) {
        if (error.message.includes('503')) {
          throw new Error(`Backend service unavailable (503). The ${subject} endpoint is not responding.`);
        } else if (error.message.includes('timeout')) {
          throw new Error(`Request timeout. The ${subject} service took too long to respond.`);
        } else if (error.message.includes('connection') || error.message.includes('DISCONNECT') || error.message.includes('closed')) {
          throw new Error(`Connection error. Unable to reach the ${subject} service.`);
        }
      }
      
      throw error;
    }
  }

  jetstream(opts?: { timeout?: number }): JetStreamClient {
    if (!this.connection) {
      throw new Error('JetStream not available - not connected to NATS');
    }
    // Large edge streams (thousands of subjects) can make consumer creation
    // exceed the 5 s default, so callers may request a longer timeout. A fresh
    // client is cheap; the cached one is returned for the default case.
    if (opts?.timeout) {
      return this.connection.jetstream({ timeout: opts.timeout });
    }
    if (!this.jsClient) {
      throw new Error('JetStream not available - not connected to NATS');
    }
    return this.jsClient;
  }

  jetstreamManager(): JetStreamManager {
    if (!this.jsManager) {
      throw new Error('JetStream Manager not available - not connected to NATS');
    }
    return this.jsManager;
  }

  isConnected(): boolean {
    return this.connection !== null && !this.connection.isClosed() && this.connectionReady;
  }

  getLastConnectedTime(): Date | null {
    return this.lastConnectedTime;
  }

  async subscribe(subject: string, callback: (data: Uint8Array) => void) {
    if (!this.connection) {
      throw new Error('Not connected to NATS');
    }

    const subscription = this.connection.subscribe(subject);
    
    (async () => {
      for await (const msg of subscription) {
        callback(msg.data);
      }
    })();

    return subscription;
  }

  async subscribeWithSubject(subject: string, callback: (data: Uint8Array, subject: string) => void) {
    if (!this.connection) {
      throw new Error('Not connected to NATS');
    }

    const subscription = this.connection.subscribe(subject);

    (async () => {
      for await (const msg of subscription) {
        callback(msg.data, msg.subject);
      }
    })();

    return subscription;
  }
}

// Export singleton instance — survive HMR by persisting on window
export const natsClient: NATSClient =
  (window as any).__natsClient ?? ((window as any).__natsClient = new NATSClient());
export const initializeNATS = async (
  servers: string[] = [import.meta.env.VITE_NATS_URL || 'wss://natsws.enlixy.com:443'],
  token?: string,
  /** NATS `user` to present with the token — "demo" in demo mode. */
  natsUser?: string
) => {
  console.log('🚀 Starting NATS initialization with servers:', servers);
  
  try {
    await natsClient.connect(servers, token, natsUser);
    console.log('✅ NATS connection successful with servers:', servers);
    
    // After successful connection, initialize NavigationHistoryService
    const { NavigationHistoryService } = await import('./NavigationHistoryService');
    await NavigationHistoryService.initialize();
    return;
    
  } catch (error) {
    console.error('💥 NATS connection failed:', error);
    if (error instanceof Error) {
      console.error('Error name:', error.name, 'Message:', error.message);
    }
  }
  
  console.log('🔄 Falling back to localStorage only mode');
  // Initialize NavigationHistoryService anyway - it will fall back to localStorage
  const { NavigationHistoryService } = await import('./NavigationHistoryService');
  await NavigationHistoryService.initialize();
};